import { useEffect, useRef } from 'react';
import { POLLING_CONFIG } from '../constants';

/**
 * Generic polling hook with timeout and success detection
 *
 * @param {Object} options
 * @param {boolean} options.enabled - Whether polling is active
 * @param {Function} options.fetchFn - Async function to call on each poll
 * @param {Function} options.successCondition - Function that returns true when polling should stop
 * @param {Function} options.onSuccess - Called when condition is met
 * @param {Function} options.onTimeout - Called when timeout is reached
 * @param {number} [options.intervalMs=3000] - Polling interval in milliseconds
 * @param {number} [options.timeoutMs=180000] - Total timeout in milliseconds
 * @returns {Object} { isPolling, pollCount, remainingTime }
 */
export const usePolling = ({
  enabled,
  fetchFn,
  successCondition,
  onSuccess,
  onTimeout,
  intervalMs = POLLING_CONFIG.INTERVAL_MS,
  timeoutMs = POLLING_CONFIG.TIMEOUT_MS,
}) => {
  const pollCountRef = useRef(0);
  const startTimeRef = useRef(null);

  useEffect(() => {
    if (!enabled) {
      pollCountRef.current = 0;
      startTimeRef.current = null;
      return;
    }

    startTimeRef.current = Date.now();

    const pollInterval = setInterval(async () => {
      try {
        const result = await fetchFn();

        if (successCondition(result)) {
          onSuccess?.(result);
          clearInterval(pollInterval);
        }

        pollCountRef.current += 1;
      } catch (error) {
        console.error('Polling error:', error);
      }
    }, intervalMs);

    const timeoutTimer = setTimeout(() => {
      clearInterval(pollInterval);
      onTimeout?.();
    }, timeoutMs);

    return () => {
      clearInterval(pollInterval);
      clearTimeout(timeoutTimer);
    };
  }, [enabled, fetchFn, successCondition, onSuccess, onTimeout, intervalMs, timeoutMs]);

};
